import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  CatalogoOpencodeSchema,
  EstadoDaConfigOpencodeSchema,
  FRASES_DOS_ERROS_DO_OPENCODE,
  provedorDoModelo,
  ROTAS,
  type CatalogoOpencode,
  type EstadoDaConfigOpencode,
} from '@remoto/shared';
import type { ProvedorOpencode } from '@remoto/protocolo';
import type { z } from 'zod';
import { chamar, ErroApi } from '../api';

/**
 * Os dados da tela de Configurações do OpenCode (F17): o que vem da API e
 * como ele é conferido antes de virar tela.
 */

export const CHAVE_DA_CONFIG = ['configuracoes-opencode'] as const;
export const CHAVE_DO_CATALOGO = 'opencode-modelos';

/** O código da resposta que não passou no schema: não é da API, é do painel. */
export const RESPOSTA_INVALIDA = 'resposta_invalida';

/**
 * As frases desta tela, pelo CÓDIGO (regra do `web.md`).
 *
 * As do OpenCode vêm prontas do shared — as mesmas das telas de criar. Somam-se
 * três que só fazem sentido aqui: a resposta que o painel recusou no parse, o
 * 404 do recurso desligado (as rotas somem com `RECURSO_OPENCODE=false`, e
 * "não encontrei este caminho na máquina", a frase geral do `nao_encontrado`,
 * mandaria procurar o defeito no lugar errado) e o 403 de quem não é ADMIN.
 */
export const FRASES_DA_TELA: Record<string, string> = {
  ...FRASES_DOS_ERROS_DO_OPENCODE,
  [RESPOSTA_INVALIDA]:
    'O servidor respondeu num formato que o painel não reconhece, e nada foi desenhado. Atualize o painel e a API para a mesma versão.',
  nao_encontrado: FRASES_DOS_ERROS_DO_OPENCODE.recurso_opencode_desligado,
  sem_permissao: 'Só um administrador mexe nas configurações do OpenCode.',
};

/**
 * Confere uma resposta contra o schema do contrato, ou lança.
 *
 * **Toda resposta desta tela passa por aqui, e é de propósito.** A chave vista
 * pela tela é `strictObject` (`{final, versao, trocadaEm}`): se um dia a API
 * mandasse o VALOR junto, por engano de serialização, o parse recusa a
 * resposta inteira — e a tela mostra um erro em vez de desenhar a chave. Um
 * `as EstadoDaConfigOpencode` aqui aceitaria qualquer coisa, e o segredo
 * chegaria ao DOM calado.
 */
export function conferir<T extends z.ZodType>(schema: T, bruto: unknown): z.infer<T> {
  const r = schema.safeParse(bruto);
  if (!r.success) {
    throw new ErroApi(0, RESPOSTA_INVALIDA, 'resposta fora do contrato');
  }
  return r.data;
}

/**
 * Recurso desligado (404), falta de permissão (403) e resposta fora do
 * contrato (0) não melhoram tentando de novo — e o 404 é justamente como a
 * tela de CRIAR descobre que o OpenCode não existe neste painel: esperar três
 * novas tentativas deixaria a opção aberta por segundos antes de fechar.
 */
function tentarDeNovo(tentativas: number, erro: unknown): boolean {
  return !(erro instanceof ErroApi && [0, 403, 404].includes(erro.status)) && tentativas < 2;
}

/**
 * O estado da tela: config, chave (só o final), máquinas e o agregado.
 *
 * Com alguma máquina esperando a chave, relê a cada 10 s: a troca termina em
 * minutos (D9), e o dono está olhando para o "X de Y" esperando o Y. No resto
 * do tempo, um minuto basta — a mudança de presença chega pelo socket.
 *
 * `ativo` existe para o diálogo de criar mestre: ele fica montado na lista o
 * tempo todo, e só deve ler a configuração quando está aberto na do OpenCode.
 */
export function usarEstadoDaConfig(ativo = true) {
  return useQuery({
    queryKey: CHAVE_DA_CONFIG,
    queryFn: async (): Promise<EstadoDaConfigOpencode> =>
      conferir(EstadoDaConfigOpencodeSchema, await chamar<unknown>(ROTAS.configuracoes.opencode)),
    enabled: ativo,
    refetchInterval: (consulta) => {
      const a = consulta.state.data?.aplicadas;
      return a && consulta.state.data?.chave && a.aplicadas < a.elegiveis ? 10_000 : 60_000;
    },
    retry: tentarDeNovo,
  });
}

/**
 * O catálogo do provedor ATIVO (F2: o NanoGPT; o OpenRouter, guardado), com
 * busca (D12): favoritos primeiro. A API lê o catálogo do provedor que a
 * configuração escolheu — não há como pedir o do outro por aqui.
 *
 * `keepPreviousData`: enquanto a busca nova não volta, a lista anterior fica
 * na tela em vez de piscar vazia a cada letra.
 */
export function usarCatalogo(busca: string, ativo = true) {
  const termo = busca.trim();
  return useQuery({
    queryKey: [CHAVE_DO_CATALOGO, termo],
    queryFn: async (): Promise<CatalogoOpencode> =>
      conferir(
        CatalogoOpencodeSchema,
        await chamar<unknown>(
          ROTAS.opencode.modelos + (termo ? `?busca=${encodeURIComponent(termo)}` : ''),
        ),
      ),
    enabled: ativo,
    // O servidor guarda o catálogo por 1 h; reler a cada foco não traz nada.
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: tentarDeNovo,
  });
}

/**
 * O valor só depois de a pessoa parar de digitar.
 *
 * Sem isto, cada letra da busca seria uma requisição — e "deepseek" são oito.
 */
export function usarAdiado<T>(valor: T, ms: number): T {
  const [adiado, setAdiado] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setAdiado(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return adiado;
}

/**
 * O provedor ATIVO, lido do catálogo (F2 da revisão de out/2026).
 *
 * **Por que do catálogo, e não da configuração:** quem cria sessão é OPERADOR,
 * e OPERADOR não lê `GET /configuracoes/opencode` (ADMIN). O catálogo ele lê, e
 * cada id ali carrega o provedor (`nano-gpt/…` é o NanoGPT, o resto é o
 * OpenRouter) — e o schema da configuração recusa modelo de outro provedor, então
 * o padrão da frota é sempre do ativo. O padrão vem primeiro; sem ele (API
 * anterior), o primeiro modelo da lista, que é do mesmo catálogo.
 *
 * `null` é "não sei" — catálogo ainda não chegou, falhou, ou veio vazio —, e a
 * tela, aí, fala no provedor de modelos sem nomear nenhum. Cair no padrão da
 * casa seria afirmar como fato o que é só a constante.
 */
export function provedorDoCatalogo(
  catalogo: Pick<CatalogoOpencode, 'padrao' | 'modelos'> | undefined,
): ProvedorOpencode | null {
  const id = catalogo?.padrao?.modelo ?? catalogo?.modelos[0]?.id;
  return id ? provedorDoModelo(id) : null;
}

/**
 * O provedor ativo para as telas que não leem a configuração (criar sessão,
 * trocar de motor, a busca do campo de modelo). A MESMA consulta do catálogo
 * sem busca — já guardada por 5 min —, e não uma requisição a mais.
 */
export function usarProvedorAtivo(ativo = true): ProvedorOpencode | null {
  const { data } = usarCatalogo('', ativo);
  return provedorDoCatalogo(data);
}
